import * as moment from 'moment';
import * as _ from "lodash";
import { useMemo, useState } from 'react';
import { ICoordinatorListByCenter, IDonorRegistration, IDonorStatusByCenter } from '@nkr/models/index';
import { ClosedLoopEntityTypeId } from '@nkr/enums/index';
import { BodyScrollEvent, CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { VoucherEducationChoice } from "@nkr/enums/voucher-education-choice";
import { AgGridReact } from 'ag-grid-react';
import { ButtonToolbar, FormControl, Glyphicon, Label, OverlayTrigger, Popover } from 'react-bootstrap';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import { DonorIntakeAction } from '../donor-intake-action';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import ActionsDropdown from '../actions-dropdown';
import ClosedLoopLinkedDonorTypeRenderer from './closed-loop-linked-donor-type-renderer';
import { hasForeignResidence } from '../../../watr/components/utils/international-eligibility';

const popoverHoverFocus = (msg: string) => (
    <Popover id='popover-trigger-hover-focus'>
        {msg}
    </Popover>
);

interface IProps {
    data: IDonorRegistration[],
    isSuperAdmin: boolean,
    centerId: number,
    subSection: string,
    gridHeight: string,
    isExpertSystemParticipant: boolean,
    onItemSelected: (id: number, action: DonorIntakeAction) => void,
    onAssignedCoordinatorChange: (regId: number, contactId: number) => void,
}

const GridPwl = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        let state = e.columnApi.getColumnState().map(c => {
            c.sort == null;
            return c;
        });

        if (props.subSection == "ALL") {
            let c = state.find(x => x.colId == 'registrationDateTime');

            if (c != null) {
                c.sort = 'desc';
            }
        } else {
            let c = state.find(x => x.colId == 'centerNextFollowUpDate');

            if (c != null) {
                c.sort = 'asc';
            }
        }

        e.columnApi.applyColumnState({
            state
        });

        e.api.sizeColumnsToFit();
    }

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // e.api.setColumnDefs(getColumnChanges());
        e.api.sizeColumnsToFit();
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const getColumnChanges = () => {
        if (props.isSuperAdmin) {
            let adminColumns: ColDef[] = [
                {
                    colId: '1',
                    field: 'rank',
                    headerName: 'R#',
                    hide: window.innerWidth < 720,
                    sortable: false,
                    width: 60,
                    pinned: 'left',
                    headerComponentFramework: (params: IHeaderParams) => <div>R</div>,
                    pinnedRowCellRendererFramework: () => <div></div>,
                    valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
                },

                {
                    field: 'source',
                    headerName: 'Source',
                    width: 130,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Source</SortableHeaderComponent>,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IDonorRegistration;

                        let style = 'default';

                        switch (data.source) {
                            case 'TXFR': style = 'primary'; break;
                            case 'NKR': style = 'info'; break;
                            case 'RDN': style = 'warning'; break;
                            case 'LIVER': style = 'danger'; break;
                            case 'DASH': style = 'default'; break;
                            case 'UDASH': style = 'warning'; break;
                            case 'MiSite': style = 'success'; break;
                            default: style = 'default'; break;
                        }

                        return <Label bsStyle={style} style={{ fontSize: '90%' }}>{props.value}</Label>;
                    }
                },

                {
                    field: 'donationType',
                    headerName: 'O',
                    width: 30,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><span title='Organ'>Org</span></SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;
                        return data.donationType == 0 ? <Label bsStyle="info" title="Kidney">K</Label> : <Label bsStyle="warning" title="Liver">L</Label>;
                    }
                },

                {
                    field: 'centerAlias',
                    headerName: 'CenterAlias',
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center<br />Alias</SortableHeaderComponent>,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IDonorRegistration;

                        let style = data.centerAlias.length > 0 ? 'success' : 'default';
                        let displayAlias = data.centerAlias.length > 0 ? data.centerAlias : 'Pending';

                        return <Label bsStyle={style} style={{ fontSize: '90%' }}>{displayAlias}</Label>;
                    }
                },

                {
                    field: '',
                    headerName: 'isInExpertSystem',
                    width: 40,
                    hide: window.innerWidth < 1400 || props.isExpertSystemParticipant == false,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>E/S</SortableHeaderComponent>,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IDonorRegistration;

                        if (data.isInExpertSystem) {
                            return (
                                <Glyphicon glyph='ok' />
                            )
                        }

                        return null;
                    }
                },

                {
                    field: 'fullName',
                    headerName: 'Name',
                    width: 250,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Name</SortableHeaderComponent>,
                },

                {
                    field: 'donorCity',
                    headerName: 'DonorCity',
                    hide: window.innerWidth < 1600,
                    width: 130,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>City</SortableHeaderComponent>,
                },

                {
                    field: 'donorState',
                    headerName: 'DonorState',
                    hide: window.innerWidth < 1000,
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>State</SortableHeaderComponent>,
                },

                {
                    field: 'donorPostalCode',
                    headerName: 'DonorPostalCode',
                    hide: window.innerWidth < 1000,
                    width: 150,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Zip</SortableHeaderComponent>,
                },

                {
                    field: '',
                    headerName: 'Reg Status',
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Reg.<br />Status</SortableHeaderComponent>,
                    valueGetter: (params: ValueGetterParams) => {
                        let data = params.data as IDonorRegistration;
                        if (data.currentRegistrationStep < 1000) { return 0; }
                        if (data.currentRegistrationStep == 3001) { return 2; }
                        if (data.currentRegistrationStep == 3000) { return 1; }
                        if (data.currentRegistrationStep >= 2000) { return 3; }
                        if (data.currentRegistrationStep >= 1000 && data.hasUsedAlcohol === null) { return 4; }
                        if (data.currentRegistrationStep >= 1000 && data.hasUsedAlcohol !== null) { return 5; }
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;
                        var msg, className, color = "black";
                        if (params.data.currentRegistrationStep == 3001) { msg = "Duplicate", className = 'glyphicon glyphicon-duplicate'; }
                        else if (params.data.currentRegistrationStep == 3000) { msg = "Transferred", className = 'glyphicon glyphicon-transfer'; }
                        else if (params.data.currentRegistrationStep >= 2000) { msg = "Ruled Out", className = 'glyphicon glyphicon-remove', color = "#CC0000"; }
                        else if (params.data.currentRegistrationStep < 1000 || params.data.hasUsedAlcohol === null) { msg = `Incomplete: ${data.currentRegistrationStepStatus}`, className = 'glyphicon glyphicon-adjust', color = "orange"; }
                        else if (params.data.currentRegistrationStep >= 1000) { msg = `Complete: ${data.currentRegistrationStepStatus}`, className = 'glyphicon glyphicon-check', color = "#00AA00"; }

                        return (
                            <ButtonToolbar>
                                <OverlayTrigger
                                    trigger={['hover', 'focus']}
                                    placement='bottom'
                                    overlay={popoverHoverFocus(msg)}>
                                    <span className={className} style={{ color: color }}></span>
                                </OverlayTrigger>
                            </ButtonToolbar>
                        );
                    }
                },

                {
                    field: 'jugStatusDesc',
                    headerName: 'jugStatusDesc',
                    hide: window.innerWidth < 1600,
                    width: 40,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Jug<br />Status</SortableHeaderComponent>,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IDonorRegistration;

                        if (data.jugStatusDesc != undefined) {
                            return (
                                <ButtonToolbar>
                                    <OverlayTrigger
                                        trigger={['hover', 'focus']}
                                        placement='bottom'
                                        overlay={popoverHoverFocus(data.jugStatusDesc)}>
                                        <span className="glyphicon glyphicon-info" style={{ color: "#0000CC" }}></span>
                                    </OverlayTrigger>
                                </ButtonToolbar>
                            );
                        }

                        return "--";
                    }
                },

                {
                    field: 'preWorkupLabStatus',
                    headerName: 'PreWorkupLabStatus',
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>PWL<br />Status</SortableHeaderComponent>,
                    valueGetter: (params: ValueGetterParams) => {
                        let data = params.data as IDonorRegistration;
                        if (data.preWorkupLabStep < 5) { return 0; }
                        if (data.preWorkupLabStep < 100) { return 2; }
                        if (data.preWorkupLabStep == 299) { return 3; }
                        if (data.preWorkupLabStep < 500) { return 4; }
                        if (data.preWorkupLabStep < 1000) { return 5; }
                        if (data.preWorkupLabStep >= 1000) { return 1; }
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;
                        var msg, className, color = "black";
                        if (data.preWorkupLabStep < 5) { msg = `Not Started (${data.preWorkupLabStatus})`; className = 'glyphicon glyphicon-unchecked'; color = "#CCCCCC"; }
                        else if (data.preWorkupLabStep < 100) { msg = `Pending Requisition (${data.preWorkupLabStatus})`; className = 'glyphicon glyphicon-pencil'; color = "#CCCCCC"; }
                        else if (data.preWorkupLabStep == 299) { msg = `Missing Labs`; className = 'glyphicon glyphicon-alert'; color = "#CC0000"; }
                        else if (data.preWorkupLabStep < 500) { msg = `Pending Completion (${data.preWorkupLabStatus})`; className = 'glyphicon glyphicon-time'; color = "#0000CC"; }
                        else if (data.preWorkupLabStep < 1000) { msg = `Complete (${data.preWorkupLabStatus})`; className = 'glyphicon glyphicon-check'; color = "#00CC00"; }
                        else if (data.preWorkupLabStep >= 1000) { msg = `Incomplete (${data.preWorkupLabStatus})`; className = 'glyphicon glyphicon-remove-sign'; color = "orange"; }

                        return (
                            <ButtonToolbar>
                                <OverlayTrigger
                                    trigger={['hover', 'focus']}
                                    placement='bottom'
                                    overlay={popoverHoverFocus(msg)}>
                                    <span className={className} style={{ color: color }}></span>
                                </OverlayTrigger>
                            </ButtonToolbar>
                        );
                    }
                },

                {
                    field: 'labKitRequestId',
                    headerName: 'LabKitRequestId',
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Lab<br />Order<br />Kit ID</SortableHeaderComponent>,
                },

                {
                    field: 'registrationDateTime',
                    headerName: 'RegistrationDate',
                    width: 140,
                    hide: window.innerWidth < 720,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date<br />Registered</SortableHeaderComponent>,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IDonorRegistration;

                        if (data.registrationDateTime != '') {
                            return (<>{moment(data.registrationDateTime).format("MM/DD/YYYY")}</>);
                        }

                        return (<>--</>);
                    },
                    comparator: Utility.dateComparator
                },

                {
                    field: 'jugRequestedDate',
                    headerName: 'jugRequestedDate',
                    width: 120,
                    hide: window.innerWidth < 1600,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Jug< br />Request<br />Date</SortableHeaderComponent>,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IDonorRegistration;
                        let date = moment(data.jugRequestedDate, "MM/DD/YYYY HH:mm:ss")

                        if (date.isValid()) {
                            return <Label bsStyle={'primary'}>{date.format("MM/DD/YYYY")}</Label>;
                        }

                        return "--";
                    },
                    comparator: Utility.dateComparator
                },

                {
                    field: 'labUploadedDateTime',
                    headerName: 'LabUploadedDateTime',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Lab<br />Uploaded<br />Date</SortableHeaderComponent>,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IDonorRegistration;
                        let displayStyle = data.labUploadedDateTime != '' ? 'success' : 'default';

                        return <Label bsStyle={displayStyle}>{data.labUploadedDateTime}</Label>;
                    },
                    valueGetter: (params: ValueGetterParams) => moment(params.data.labUploadedDateTime).isValid() ? moment(params.data.labUploadedDateTime).format('MM/DD/YY') : null,
                },

                {
                    field: 'daysPending',
                    headerName: 'DaysPending',
                    valueGetter: (params: ValueGetterParams) => params.data.daysPending > 0 ? params.data.daysPending : "--",
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Days<br />Pend<br />Ctr</SortableHeaderComponent>,
                    comparator: (a: any, b: any) => {
                        const aVal = a === '--' ? -1 : Number(a);
                        const bVal = b === '--' ? -1 : Number(b);
                        return aVal - bVal;
                    },
                    width: 150,
                },

                {
                    field: 'countryOfResidenceId',
                    headerName: 'Intl Donor',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Intl<br />Donor</SortableHeaderComponent>,
                    width: 90,
                    sortable: true,
                    hide: false,
                    valueGetter: (params: ValueGetterParams) => {
                        const data = params.data as IDonorRegistration;
                        return hasForeignResidence(data.countryOfResidenceId) ? 'Yes' : 'No';
                    },
                    cellRendererFramework: (params: ICellRendererParams) => {
                        const data = params.data as IDonorRegistration;
                        const isIntlDonor = hasForeignResidence(data.countryOfResidenceId);
                        return <Label bsStyle={isIntlDonor ? 'success' : 'default'}>{isIntlDonor ? 'Yes' : 'No'}</Label>;
                    }
                },

                {
                    field: 'actionId',
                    headerName: 'Actions',
                    width: 120,
                    cellClass: 'action-menu',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actions</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;
                        let { centerId } = data;

                        return (
                            <ActionsDropdown
                                {...params}
                                // centerId={centerId}
                                donor={data}
                                showLinkToDetails={true}
                                showBsMenu={false}
                                isSuperAdmin={props.isSuperAdmin}
                                onMenuToggle={handleMenuToggle}
                                onItemSelected={props.onItemSelected}
                            />
                        )
                    }
                },
            ];

            return adminColumns;
        } else {
            let cols: ColDef[] = [
                {
                    colId: '1',
                    field: 'rank',
                    headerName: 'R#',
                    sortable: false,
                    width: 60,
                    pinned: 'left',
                    headerComponentFramework: (params: IHeaderParams) => <div>R#</div>,
                    pinnedRowCellRendererFramework: () => <div></div>,
                    valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
                },
                {
                    field: 'source',
                    headerName: 'Source',
                    width: 130,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Source</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;

                        let style = 'default';

                        switch (data.source) {
                            case 'TXFR': style = 'primary'; break;
                            case 'NKR': style = 'info'; break;
                            case 'RDN': style = 'warning'; break;
                            case 'LIVER': style = 'danger'; break;
                            case 'DASH': style = 'default'; break;
                            case 'UDASH': style = 'warning'; break;
                            case 'MiSite': style = 'success'; break;
                            default: style = 'default'; break;
                        }

                        return <Label bsStyle={style} style={{ fontSize: '90%' }}>{params.value}</Label>;
                    }
                },
                {
                    field: 'fullName',
                    headerName: 'Name',
                    width: 220,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
                },
                {
                    field: 'registrationDateTime',
                    headerName: 'RegistrationDate',
                    width: 170,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registration<br />Date</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;
                        return <>{data.registrationDateTime}</>;
                    },
                    comparator: Utility.dateComparator
                },
                {
                    field: 'preWorkupLabStatus',
                    headerName: 'PreWorkupLabStatus',
                    width: 150,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>PWL<br />Status</SortableHeaderComponent>,
                },
                {
                    field: 'currentRegistrationStepStatus',
                    headerName: 'currentRegistrationStepStatus',
                    width: 150,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registration<br />Status</SortableHeaderComponent>,
                },
                {
                    field: '',
                    headerName: 'isInExpertSystem',
                    width: 90,
                    hide: props.isExpertSystemParticipant == false,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Expert <br /> System</SortableHeaderComponent>,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IDonorRegistration;

                        if (data.isInExpertSystem) {
                            return (
                                <Glyphicon glyph='ok' />
                            )
                        }

                        return null;
                    }
                },
                {
                    field: 'type',
                    headerName: 'Type',
                    width: 80,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;

                        let vem = data.voucherEducationChoiceId == VoucherEducationChoice.WantsToLearnMore ? 'i' : '';

                        let style = '';
                        let label = data.type;

                        switch (data.type) {
                            case 'SV': style = 'success'; label = data.type + "" + vem; break;
                            case 'FV': style = 'success'; label = data.type + "" + vem; break;
                            case 'NDD': style = 'info'; break;
                            case 'DD': style = 'default'; break;
                            default: style = 'default'; break;
                        }

                        return <Label bsStyle={style} style={{ fontSize: '90%' }}>{label}</Label>;
                    }
                },
                {
                    field: 'closeLoopLinkedDonorType',
                    headerName: 'Type Donated',
                    hide: props.subSection != 'ALL',
                    width: 80,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type <br />Donated</SortableHeaderComponent>,
                    comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                        let returnValue = valueA - valueB;

                        if (nodeA.data.closeLoopEntityTypeId == ClosedLoopEntityTypeId.Direct && (valueA - valueB == 0)) {
                            return 5;
                        };

                        return returnValue;
                    },
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IDonorRegistration;
                        return <ClosedLoopLinkedDonorTypeRenderer {...data} />
                    }
                },
                {
                    field: 'closeLoopEpletMismatch',
                    headerName: 'Eplet MM',
                    hide: props.subSection != 'ALL',
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Eplet<br />MM</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;

                        let style = '';
                        let label = data.closeLoopEpletMismatch;

                        if (data.closeLoopEpletMismatch == "0 MM") {
                            style = 'success';
                        } else if (data.closeLoopEpletMismatch == "Low") {
                            style = 'success';
                        } else if (data.closeLoopEpletMismatch == "Med") {
                            style = 'warning';
                        } else if (data.closeLoopEpletMismatch == "High") {
                            style = 'danger';
                        }

                        if (style.length > 0) {
                            return <Label bsStyle={style} style={{ fontSize: '90%', color: label == '--' ? "black" : "white" }}>{label}</Label>;
                        } else {
                            return <Label style={{ fontSize: '90%', color: label == '--' ? "black" : "white" }}>{label}</Label>;
                        }
                    }
                },
                {
                    field: 'labStatus',
                    headerName: '',
                    width: 20,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Labs</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;

                        if (data.isRepeat || data.hasFile) {
                            let msg = '';
                            let className = '';

                            if (data.isRepeat && data.hasFile) {
                                msg = 'Repeat Labs Complete';
                                className = 'glyphicon glyphicon-star-empty';
                            } else if (data.isRepeat) {
                                msg = 'Repeat Labs In Progress';
                                className = 'glyphicon glyphicon-repeat';
                            } else if (data.hasFile) {
                                msg = 'Labs Complete';
                                className = 'glyphicon glyphicon-star';
                            }

                            return (
                                <ButtonToolbar>
                                    <OverlayTrigger
                                        trigger={['hover', 'focus']}
                                        placement='bottom'
                                        overlay={popoverHoverFocus(msg)}>
                                        <span className={className}></span>
                                    </OverlayTrigger>
                                </ButtonToolbar>
                            );
                        }

                        return <></>;
                    }
                },
                {
                    field: '',
                    headerName: 'Status',
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;
                        let holdStyle = data.isOnHold ? {} : { display: 'none' };
                        let alcoholUseStyle = data.hasUsedAlcohol ? { display: 'none', color: 'red' } : { color: 'red' };

                        return <>
                            <ButtonToolbar>
                                <span className='label label-warning' style={holdStyle}>Hold</span> {' '}
                                <OverlayTrigger
                                    trigger={['hover', 'focus']}
                                    placement='bottom'
                                    overlay={popoverHoverFocus('Appears to be Incomplete/Expired')}>
                                    <span className='glyphicon glyphicon-adjust' style={alcoholUseStyle}></span>
                                </OverlayTrigger>
                            </ButtonToolbar>
                        </>;
                    },
                    valueGetter: (params: ValueGetterParams) => params.data.isOnHold && !params.data.hasUsedAlcohol ? 0 : params.data.isOnHold ? 1 : !params.data.hasUsedAlcohol ? 2 : 3,
                },
                {
                    field: 'historyCompleteDateTime',
                    headerName: 'Referred Date',
                    hide: props.subSection != 'ALL',
                    width: 150,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Referred<br />Date</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;

                        if (data.historyCompleteDateTime == '') {
                            return '';
                        }

                        return <>{data.historyCompleteDateTime}</>
                    },
                    comparator: Utility.dateComparator
                },
                {
                    colId: 'centerNextFollowUpDate',
                    field: 'centerNextFollowUpDate',
                    headerName: 'Next Follow-Up',
                    width: 150,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Next<br />Follow-Up<br />Date</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;

                        let nextFollowUpDate = moment(data.centerNextFollowUpDate, 'MM/DD/YYYY', true);

                        if (nextFollowUpDate.isBefore(moment(), 'day')) {
                            return <Label bsStyle='danger' style={{ fontSize: '100%' }}>{data.centerNextFollowUpDate}</Label>
                        }

                        if (nextFollowUpDate.isAfter(moment(), 'day')) {
                            return <Label bsStyle='success' style={{ fontSize: '100%' }}>{data.centerNextFollowUpDate}</Label>
                        }

                        if (nextFollowUpDate.isSame(moment(), 'day')) {
                            return <Label bsStyle='warning' style={{ fontSize: '100%' }}>{data.centerNextFollowUpDate}</Label>
                        }

                        return null;
                    },
                    comparator: Utility.dateComparator
                },

                {
                    field: 'recipFullName',
                    headerName: 'Intended Recipient',
                    width: 200,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Intended <br />Recipient</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;

                        if (data.recipFullName.length == 0) {
                            if (data.type == 'FV') {
                                return <Label bsStyle='warning' style={{ fontSize: '100%' }}>N/A - FV</Label>;
                            } else if (data.type == 'NDD') {
                                return <Label bsStyle='warning' style={{ fontSize: '100%' }}>N/A - NDD</Label>;
                            } else {
                                return <Label bsStyle='warning' style={{ fontSize: '100%' }}>N/A</Label>
                            }
                        } else {
                            return <ButtonToolbar>{data.recipFullName} {' '}
                                <OverlayTrigger
                                    trigger={['hover', 'focus']}
                                    placement='bottom'
                                    overlay={popoverHoverFocus(data.recipientLocation)}>
                                    <span className='glyphicon glyphicon-info-sign' title=''></span>
                                </OverlayTrigger>
                            </ButtonToolbar>
                        }
                    }
                },

                {
                    field: 'recipDob',
                    headerName: 'Intended Recipient DOB',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Intended <br />Recipient<br />DOB</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;
                        return data.recipDob == null ? '--' : data.recipDob;
                    },
                    comparator: Utility.dateComparator,
                },
                {
                    field: 'closeLoopSurgeryDateTime',
                    headerName: 'Donation Date',
                    hide: props.subSection != 'ALL',
                    width: 130,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donation<br />Date</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;

                        if (data.closeLoopSurgeryDateTime == null) {
                            return '--';
                        }

                        return <>{data.closeLoopSurgeryDateTime}</>
                    },
                    comparator: Utility.dateComparator
                },

                {
                    colId: 'actionMenu',
                    field: 'actionId',
                    headerName: 'Actions',
                    width: 110,
                    cellClass: 'action-menu',
                    sortable: false,
                    headerComponentFramework: (params: IHeaderParams) => <div {...params}>Actions</div>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IDonorRegistration;
                        let { centerId } = data;

                        return (
                            <ActionsDropdown
                                {...params}
                                // centerId={centerId}
                                donor={data}
                                showLinkToDetails={true}
                                showBsMenu={false}
                                isSuperAdmin={props.isSuperAdmin}
                                onMenuToggle={handleMenuToggle}
                                onItemSelected={props.onItemSelected}
                            />
                        );
                    }
                }
            ];

            return cols;
        }
    };

    const getHeight = () => {
        let height = '60vh';

        if (props.subSection == 'ALL') {
            height = '45vh';
        }

        if (props.centerId == 0) {
            height = '65vh'
        }

        return height;
    };

    const columnDefs = useMemo(() => getColumnChanges(), [props.isSuperAdmin]);
    const rowData = useMemo(() => props.data, [props.data]);
    const height = getHeight();

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ height: props.subSection == 'ALL' ? props.gridHeight : '70vh' }}>
            <AgGridReact
                defaultColDef={{
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={props.isSuperAdmin ? 55 : 60}
                rowHeight={34}
                enableCellChangeFlash={true}
                suppressCellSelection={true}
                rowStyle={{ overflow: 'visible' }}
                noRowsOverlayComponentFramework={() => <div>...</div>}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onBodyScroll={onBodyScroll}
                onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
            />
        </GridWrapper>
    )
}

export default GridPwl;